import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../../../services/api";
import { BackButton } from "../../../components/button/back-button";
import { useToast } from "../../../contexts/toast/toast-context";
import { getApiErrorMessage } from "../../../utils";
import { ViewConversationModal } from "../../../components/view-conversation-modal";
import type { Conversation } from "../../../types/conversation";
import { ProtocolAccess } from "./access";
import { PartySelect } from "./party-select";
import {
  type Protocol,
  priorityLabels,
  statusLabels,
  slaLabels,
  protocolDate,
} from "../../../types/protocol";
import { ProtocolAttachmentsSection } from "./attachments-section";
const eventLabels: Record<string, string> = {
  CREATED: "Criado",
  STATUS_CHANGED: "Status alterado",
  ASSIGNED: "Responsável alterado",
  PRIORITY_CHANGED: "Prioridade alterada",
  COMMENT_ADDED: "Comentário adicionado",
  RESOLVED: "Resolvido",
  CANCELED: "Cancelado",
};

const statusTone: Record<Protocol["status"], string> = {
  OPEN: "border-blue-200 bg-blue-50 text-blue-800",
  IN_PROGRESS: "border-amber-200 bg-amber-50 text-amber-800",
  RESOLVED: "border-emerald-200 bg-emerald-50 text-emerald-800",
  CANCELED: "border-slate-200 bg-slate-100 text-slate-700",
};

const priorityTone: Record<Protocol["priority"], string> = {
  LOW: "border-slate-200 bg-slate-50 text-slate-700",
  NORMAL: "border-blue-200 bg-blue-50 text-blue-800",
  HIGH: "border-amber-200 bg-amber-50 text-amber-800",
  CRITICAL: "border-red-200 bg-red-50 text-red-800",
};

const slaTone: Record<NonNullable<Protocol["sla"]>, string> = {
  ADEQUATE: "border-emerald-200 bg-emerald-50 text-emerald-800",
  WARNING: "border-amber-200 bg-amber-50 text-amber-800",
  OVERDUE: "border-red-200 bg-red-50 text-red-800",
};

const indicatorClass =
  "rounded-xl border p-3 text-slate-800 shadow-sm md:min-h-20";
export default function ProtocolDetailPage() {
  return (
    <ProtocolAccess>
      <ProtocolDetail />
    </ProtocolAccess>
  );
}
function ProtocolDetail() {
  const { id } = useParams();
  const { error, success } = useToast();
  const [p, setP] = useState<Protocol | null>(null);
  const [loading, setLoading] = useState(true),
    [busy, setBusy] = useState(false),
    [failed, setFailed] = useState(false);
  const [assigned, setAssigned] = useState(""),
    [text, setText] = useState(""),
    [action, setAction] = useState("comments");
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);
  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const res = await api.get(`/protocols/${id}`);
      setP(res.data);
      setAssigned(res.data.assignedToSystemUserId || "");
    } catch (err) {
      setFailed(true);
      error(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [id, error]);
  useEffect(() => {
    void load();
  }, [load]);
  const mutate = async (
    operation: string,
    data: Record<string, unknown> = {},
  ) => {
    if (!p || busy) return;
    setBusy(true);
    try {
      await api.request({
        url: `/protocols/${p.id}/${operation}`,
        method: operation === "comments" ? "POST" : "PATCH",
        data: { version: p.version, ...data },
      });
      success("Protocolo atualizado.");
      setText("");
      await load();
    } catch (err) {
      error(getApiErrorMessage(err));
      await load();
    } finally {
      setBusy(false);
    }
  };
  const origin = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/protocols/${id}/origin`);
      setConversation(res.data);
    } catch (err) {
      error(getApiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const submitText = (e: FormEvent) => {
    e.preventDefault();
    void mutate(action, { text: text.trim() });
  };
  if (loading)
    return (
      <p role="status" className="p-4">
        Carregando protocolo…
      </p>
    );
  if (failed || !p)
    return (
      <div className="app-page" role="alert">
        Não foi possível carregar.{" "}
        <button className="btn btn-secondary" onClick={() => void load()}>
          Tentar novamente
        </button>
      </div>
    );
  const closed = p.status === "RESOLVED" || p.status === "CANCELED";
  const actorName = (id?: string, fallback = "Sistema") =>
    id ? p.actorNames?.[id] || id : fallback;
  const changed = (value: unknown) => {
    const key = String(value ?? "Não informado");
    return (
      statusLabels[key as keyof typeof statusLabels] ||
      priorityLabels[key as keyof typeof priorityLabels] ||
      p.actorNames?.[key] ||
      key
    );
  };
  return (
    <div className="app-page space-y-4">
      <BackButton />
      <h1 className="app-page-title">Protocolo {p.protocolNumber}</h1>
      <section className="app-card p-4 space-y-3">
        <h2 className="app-section-title">{p.title}</h2>
        <dl
          className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3"
          aria-label="Indicadores do protocolo"
        >
          <div className={`${indicatorClass} ${statusTone[p.status]}`}>
            <dt className="text-xs font-semibold uppercase tracking-wide opacity-75">
              Status
            </dt>
            <dd className="mt-2 font-semibold">{statusLabels[p.status]}</dd>
          </div>
          <div className={`${indicatorClass} ${priorityTone[p.priority]}`}>
            <dt className="text-xs font-semibold uppercase tracking-wide opacity-75">
              Prioridade
            </dt>
            <dd className="mt-2 font-semibold">{priorityLabels[p.priority]}</dd>
          </div>
          <div className={indicatorClass}>
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Responsável
            </dt>
            <dd className="mt-2 font-semibold">
              {p.assignedToName || "Sem responsável"}
            </dd>
          </div>
          <div className={indicatorClass}>
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Abertura
            </dt>
            <dd className="mt-2 font-semibold">{protocolDate(p.createdAt)}</dd>
          </div>
          <div className={indicatorClass}>
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Prazo (São Paulo)
            </dt>
            <dd className="mt-2 font-semibold">{protocolDate(p.dueAt)}</dd>
          </div>
          <div
            className={`${indicatorClass} ${
              p.sla
                ? slaTone[p.sla]
                : "border-slate-200 bg-slate-50 text-slate-700"
            }`}
          >
            <dt className="text-xs font-semibold uppercase tracking-wide opacity-75">
              SLA
            </dt>
            <dd className="mt-2 font-semibold">
              {p.sla ? slaLabels[p.sla] : "Não se aplica"}
            </dd>
          </div>
        </dl>
        <div className="border-t border-slate-200 pt-4">
          <h3 className="text-sm font-semibold text-slate-700">Descrição</h3>
          <p className="mt-2 whitespace-pre-wrap break-words">
            {p.description}
          </p>
        </div>
        <dl className="border-t border-slate-200 pt-3">
          <div>
            <dt className="text-sm font-semibold text-slate-700">
              Solicitante
            </dt>
            <dd className="mt-1">
              {p.requesterId ? (
                <Link
                  className="text-blue-700 underline"
                  to={`/painel/contatos/visualizar/${p.requesterId}`}
                >
                  {p.requesterName || p.requesterId}
                </Link>
              ) : (
                "Não informado"
              )}
            </dd>
          </div>
        </dl>
        {p.conversationId && (
          <button
            className="btn btn-secondary"
            disabled={busy}
            onClick={() => void origin()}
          >
            Ver atendimento de origem
          </button>
        )}
        {p.closedAt && <p>Encerrado em {protocolDate(p.closedAt)}</p>}
        {p.resolution && (
          <p className="whitespace-pre-wrap">Resolução: {p.resolution}</p>
        )}
        {p.cancellationReason && (
          <p className="whitespace-pre-wrap">
            Cancelamento: {p.cancellationReason}
          </p>
        )}
      </section>
      {!closed && (
        <section className="app-card p-4 space-y-4">
          <h2 className="app-section-title">Acompanhamento</h2>
          <div className="flex flex-wrap gap-2">
            {!p.assignedToSystemUserId && (
              <button
                className="btn btn-secondary"
                disabled={busy}
                onClick={() => void mutate("assign")}
              >
                Assumir protocolo
              </button>
            )}
            {p.status === "OPEN" && (
              <button
                className="btn btn-primary"
                disabled={busy}
                onClick={() => void mutate("start")}
              >
                Iniciar atendimento
              </button>
            )}
          </div>
          <fieldset disabled={busy} className="grid gap-3 md:grid-cols-2">
            <div>
              <PartySelect
                kind="system-user"
                label="Atribuir responsável"
                value={assigned}
                onChange={setAssigned}
              />
              <button
                className="btn btn-secondary mt-2"
                disabled={!assigned || assigned === p.assignedToSystemUserId}
                onClick={() =>
                  void mutate("assign", { assignedToSystemUserId: assigned })
                }
              >
                Atribuir
              </button>
            </div>
            <label className="form-label">
              Alterar prioridade
              <select
                className="form-input"
                value={p.priority}
                onChange={(e) =>
                  void mutate("priority", { priority: e.target.value })
                }
              >
                {Object.entries(priorityLabels).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
              <span className="text-xs text-slate-500">
                O prazo será recalculado desde a abertura conforme o SLA da nova
                prioridade.
              </span>
            </label>
          </fieldset>
          <form onSubmit={submitText} className="space-y-2">
            <fieldset disabled={busy}>
              <label className="form-label block">
                Ação
                <select
                  className="form-input"
                  value={action}
                  onChange={(e) => setAction(e.target.value)}
                >
                  <option value="comments">Comentário interno</option>
                  <option value="resolve">Resolver protocolo</option>
                  <option value="cancel">Cancelar protocolo</option>
                </select>
              </label>
              <label className="form-label block">
                {action === "resolve"
                  ? "Descrição da resolução"
                  : action === "cancel"
                    ? "Justificativa de cancelamento"
                    : "Comentário interno"}
                <textarea
                  className="form-input"
                  required
                  maxLength={10000}
                  rows={4}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                />
              </label>
              {action !== "comments" && (
                <p className="text-sm">O encerramento é definitivo.</p>
              )}
              <button
                className="btn btn-primary"
                disabled={busy || !text.trim()}
              >
                {busy
                  ? "Salvando…"
                  : action === "resolve"
                    ? "Resolver"
                    : action === "cancel"
                      ? "Cancelar protocolo"
                      : "Adicionar comentário"}
              </button>
            </fieldset>
          </form>
        </section>
      )}
      <ProtocolAttachmentsSection
        protocolId={p.id}
        version={p.version}
        readOnly={closed}
        attachments={p.attachments}
        selectedFiles={attachmentFiles}
        onSelectedFilesChange={setAttachmentFiles}
        onProtocolChange={setP}
        onMutationRejected={load}
      />
      <section className="app-card p-4 space-y-2">
        <h2 className="app-section-title">Comentários internos</h2>
        {!p.comments?.length && <p>Nenhum comentário.</p>}
        {p.comments?.map((c) => (
          <article className="border-b py-2" key={c.id}>
            <p className="text-sm text-slate-500">
              {actorName(c.actorSystemUserId)} · {protocolDate(c.createdAt)}
            </p>
            <p className="whitespace-pre-wrap break-words">{c.text}</p>
          </article>
        ))}
      </section>
      <section className="app-card p-4 space-y-2">
        <h2 className="app-section-title">Histórico</h2>
        <ol>
          {p.events?.map((e) => (
            <li className="border-b py-2" key={e.id}>
              {eventLabels[e.type] || e.type} · {protocolDate(e.createdAt)} ·{" "}
              {actorName(
                e.actorSystemUserId,
                e.actorType === "AI" ? "IA" : "Sistema",
              )}
              {e.changes && ("from" in e.changes || "to" in e.changes) && (
                <p className="text-sm">
                  {changed(e.changes.from)} → {changed(e.changes.to)}
                </p>
              )}
            </li>
          ))}
        </ol>
      </section>
      <ViewConversationModal
        isOpen={!!conversation}
        conversation={conversation}
        onClose={() => setConversation(null)}
      />
    </div>
  );
}
